import React, { useState, useRef } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { fetchWorkerDetails, uploadImageToS3, setOnboarding } from 'modules/state-mgmt/onboarding';
import { createGuid, prettifyErrorResponse } from 'utils';
import { getImageFormat } from 'utils/files';
import { Services } from 'services';

import { actionMapForOtherLoaders } from 'constants/actions';

interface IUploadProfilePictureParams {
  imageSrc: string;
}

type UseUploadProfilePicture = (params?: { successCallback?: () => void; failureCallback?: (error: any) => void }) => {
  isLoading: boolean;
  error: any;
  uploadProfilePicture: (params: IUploadProfilePictureParams) => void;
};

const useUploadProfilePicture: UseUploadProfilePicture = ({ successCallback, failureCallback } = {}) => {
  const [state, setState] = useState({
    isLoading: false,
    error: null,
  });

  const { isLoading, error } = state;

  const { userId, languageCode, isWorker, workerId, isOffline, uploadingDetails } = useAppSelector(state => ({
    userId: state.onboarding.userId,
    languageCode: state.app?.languageCode,
    isWorker: !state.user?.userId || state.user?.isWorker,
    workerId: state.onboarding.id,
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    uploadingDetails: state.onboarding.uploadingDetails,
  }));

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const fileNameRef = useRef(createGuid());

  const getWorkerProfileDetails = () => {
    dispatch(fetchWorkerDetails({ workerId, languageCode: languageCode }))
      .then(res => {
        if (res.type.includes('fulfilled')) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: null,
          }));
          successCallback();
        } else {
          enqueueSnackbar(prettifyErrorResponse(res?.payload), {
            variant: 'error',
          });
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: res?.payload,
          }));
          failureCallback?.(res?.payload);
        }
      })
      .catch(err => {
        enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err,
        }));
        failureCallback?.(err);
      });
  };

  const uploadProfilePicture = ({ imageSrc }: IUploadProfilePictureParams) => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: [],
    }));

    if (isOffline && !isWorker) {
      // Explicit Offline Flow (Middleware handles persistence)
      dispatch(
        setOnboarding({
          pictureUrl: imageSrc,
          uploadingDetails: {
            ...(uploadingDetails || {}),
            isOffline: true,
            pendingUploads: { ...(uploadingDetails?.pendingUploads || {}), profilePicture: true },
          },
        }),
      );
      setState(prev => ({ ...prev, isLoading: false }));
      successCallback?.();
      return;
    }

    const fileExtension = getImageFormat(imageSrc);
    dispatch(
      uploadImageToS3({
        ...(isWorker
          ? {
              apiFunction: Services.uploadWorkerImage,
              apiFunctionArgs: [
                workerId,
                {
                  fileName: `${fileNameRef.current}.${fileExtension}`,
                },
                languageCode,
              ],
            }
          : {
              apiFunction: Services.uploadImage,
              apiFunctionArgs: [
                userId,
                {
                  fileName: `${fileNameRef.current}.${fileExtension}`,
                },
                languageCode,
              ],
            }),
        image: imageSrc,
        languageCode: languageCode,
        customLoadingKey: actionMapForOtherLoaders.uploadProfilePicture,
      }),
    )
      .then(res => {
        if (res.type.includes('fulfilled')) {
          getWorkerProfileDetails();
        } else {
          enqueueSnackbar(t('capturePhoto.errors.photoUploadFailed'), {
            variant: 'error',
          });
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: res?.payload,
          }));
          failureCallback?.(res?.payload);
        }
      })
      .catch(err => {
        enqueueSnackbar(t('capturePhoto.errors.photoUploadFailed'), { variant: 'error' });
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err,
        }));
        failureCallback?.(err);
      });
  };

  return { isLoading, error, uploadProfilePicture };
};

export default useUploadProfilePicture;
